// src/QuestSystem.js
// Sistema de misiones diarias generado dinámicamente según estado del jugador (Fase G4).

const QUEST_TEMPLATES = {
    rounds: {
        id: 'q_rounds_complete',
        title: 'Pase de Visita',
        desc: 'Completa 1 Pase de Visita sin errores.',
        type: 'study',
        target: { count: 1, event: 'rounds_complete' },
        reward: { credits: 50, xp: 100 }
    },
    weakness: (domainName) => ({
        id: `q_weak_${domainName.replace(/\s+/g, '_')}`,
        title: 'Estudio Dirigido',
        desc: `Mejora tu precisión en ${domainName} (responde 3 casos correctos).`,
        type: 'competence',
        target: { count: 3, event: 'correct_domain', domain: domainName },
        reward: { credits: 100, xp: 80 }
    }),
    general: [
        {
            id: 'q_guard_complete',
            title: 'Guardia Completada',
            desc: 'Completa 1 guardia general.',
            type: 'service',
            target: { count: 1, event: 'guard_complete' },
            reward: { credits: 80, xp: 30 }
        },
        {
            id: 'q_no_lethal',
            title: 'Sin Bajas',
            desc: 'Completa una guardia sin errores letales.',
            type: 'competence',
            target: { count: 1, event: 'guard_no_lethal' },
            reward: { credits: 150, xp: 50 }
        }
    ]
};

export class QuestSystem {
    /**
     * Generates a daily quest set tailored to the player's weak points.
     */
    static generateDailyQuests(dayNumber, economyState) {
        const quests = [];
        
        // 1. Pase de visita si hay casos fallados o perlas
        const failedCases = economyState?.adaptiveState?.failedCases || [];
        const forgotten = economyState?.discoveredPearls ? Object.keys(economyState.discoveredPearls).filter(k => economyState.discoveredPearls[k].forgotten) : [];
        if (failedCases.length > 0 || forgotten.length > 0) {
            quests.push(QUEST_TEMPLATES.rounds);
        } else {
            quests.push(QUEST_TEMPLATES.general[0]); // Fallback
        }

        // 2. Misión de debilidad basada en FSRS
        if (economyState?.caseHistory) {
            const domains = [];
            for (const [key, val] of Object.entries(economyState.caseHistory)) {
                if (key.startsWith('__topic_') && val.seen > 0) {
                    const acc = (val.correct || 0) / val.seen;
                    domains.push({ name: key.replace('__topic_', ''), acc });
                }
            }
            if (domains.length > 0) {
                domains.sort((a, b) => a.acc - b.acc); // El peor primero
                quests.push(QUEST_TEMPLATES.weakness(domains[0].name));
            } else {
                quests.push(QUEST_TEMPLATES.general[1]); // Fallback
            }
        } else {
            quests.push(QUEST_TEMPLATES.general[1]);
        }

        // 3. Misión de participación genérica
        quests.push({
            id: 'q_correct_5',
            title: 'Diagnósticos Certeros',
            desc: 'Responde 5 preguntas correctas en total hoy.',
            type: 'service',
            target: { count: 5, event: 'correct' },
            reward: { credits: 120, xp: 45 }
        });

        return quests.map(q => ({
            ...q,
            progress: 0,
            completed: false
        }));
    }

    /**
     * Check and update quest progress based on a game event.
     * Returns true if a quest was newly completed.
     */
    static checkProgress(quest, eventData) {
        if (quest.completed) return false;

        const target = quest.target;
        let newlyCompleted = false;

        if (target.event === 'correct' && eventData.result === 'correct') {
            quest.progress++;
            if (quest.progress >= target.count) { quest.completed = true; newlyCompleted = true; }
        }
        
        if (target.event === 'correct_domain' && eventData.result === 'correct' && eventData.domain === target.domain) {
            quest.progress++;
            if (quest.progress >= target.count) { quest.completed = true; newlyCompleted = true; }
        }

        if (target.event === 'guard_complete' && eventData.guardComplete) {
            quest.progress++;
            if (quest.progress >= target.count) { quest.completed = true; newlyCompleted = true; }
        }

        if (target.event === 'guard_no_lethal' && eventData.guardComplete && !eventData.hadLethal) {
            quest.progress++;
            if (quest.progress >= target.count) { quest.completed = true; newlyCompleted = true; }
        }
        
        if (target.event === 'rounds_complete' && eventData.roundsComplete) {
            quest.progress++;
            if (quest.progress >= target.count) { quest.completed = true; newlyCompleted = true; }
        }

        return newlyCompleted;
    }
}
